/**
 * Chat API Service
 *
 * @module core/chat/services
 * @version 1.1
 */

import axios from 'axios';
import type { ChatConversation, ChatMessage, ChatResponse, SendMessageRequest } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
const CHAT_REQUEST_TIMEOUT_MS = Number(import.meta.env.VITE_CHAT_REQUEST_TIMEOUT_MS || 60000);

const chatClient = axios.create({
  baseURL: `${API_URL}/chat`,
  timeout: Number.isFinite(CHAT_REQUEST_TIMEOUT_MS) ? CHAT_REQUEST_TIMEOUT_MS : 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

chatClient.interceptors.request.use((config) => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage);
      if (parsed.state?.token) {
        config.headers.Authorization = `Bearer ${parsed.state.token}`;
      }
    } catch (err) {
      console.warn('[ChatAPI] Error al parsear auth-storage:', err);
    }
  }
  return config;
});

function mapMessage(message: Omit<ChatMessage, 'timestamp'> & { timestamp: string | Date }): ChatMessage {
  return {
    ...message,
    timestamp: new Date(message.timestamp),
  };
}

function mapConversation(
  conversation: Omit<ChatConversation, 'messages' | 'createdAt' | 'updatedAt'> & {
    messages: Array<Omit<ChatMessage, 'timestamp'> & { timestamp: string | Date }>;
    createdAt: string | Date;
    updatedAt: string | Date;
  },
): ChatConversation {
  return {
    ...conversation,
    createdAt: new Date(conversation.createdAt),
    updatedAt: new Date(conversation.updatedAt),
    messages: conversation.messages.map(mapMessage),
  };
}

function toApiError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const message =
      (typeof error.response?.data?.message === 'string' && error.response.data.message) ||
      error.message ||
      'Error al comunicarse con el asistente';
    return new Error(message);
  }

  return error instanceof Error ? error : new Error('Error al comunicarse con el asistente');
}

export const chatApi = {
  async sendMessage(data: SendMessageRequest): Promise<ChatResponse> {
    try {
      const response = await chatClient.post<ChatResponse>('/messages', data);
      return {
        ...response.data,
        message: mapMessage(response.data.message as ChatMessage & { timestamp: string | Date }),
      };
    } catch (error) {
      throw toApiError(error);
    }
  },

  async getCurrentConversation(
    projectId: string,
    options?: { limit?: number; before?: string },
  ): Promise<ChatConversation | null> {
    try {
      const response = await chatClient.get<ChatConversation | null>('/conversations/current', {
        params: {
          projectId,
          ...(options?.limit ? { limit: options.limit } : {}),
          ...(options?.before ? { before: options.before } : {}),
        },
      });
      return response.data ? mapConversation(response.data as any) : null;
    } catch (error) {
      throw toApiError(error);
    }
  },

  async getConversations(projectId: string): Promise<ChatConversation[]> {
    try {
      const response = await chatClient.get<ChatConversation[]>('/conversations', {
        params: { projectId },
      });
      return response.data.map((conversation) => mapConversation(conversation as any));
    } catch (error) {
      throw toApiError(error);
    }
  },

  async getConversation(
    projectId: string,
    conversationId: string,
    options?: { limit?: number; before?: string },
  ): Promise<ChatConversation | null> {
    try {
      const response = await chatClient.get<ChatConversation | null>(`/conversations/${conversationId}`, {
        params: {
          projectId,
          ...(options?.limit ? { limit: options.limit } : {}),
          ...(options?.before ? { before: options.before } : {}),
        },
      });
      return response.data ? mapConversation(response.data as any) : null;
    } catch (error) {
      throw toApiError(error);
    }
  },

  async deleteConversation(projectId: string, conversationId: string): Promise<void> {
    try {
      await chatClient.delete(`/conversations/${conversationId}`, {
        params: { projectId },
      });
    } catch (error) {
      throw toApiError(error);
    }
  },

  async clearProjectHistory(projectId: string): Promise<void> {
    try {
      await chatClient.delete('/conversations', {
        params: { projectId },
      });
    } catch (error) {
      throw toApiError(error);
    }
  },

  async getProjectUsage(projectId: string, budgetLimit?: number): Promise<any> {
    const params: any = { projectId };
    if (budgetLimit) {
      params.budgetLimit = budgetLimit.toString();
    }

    try {
      const response = await chatClient.get('/usage/project', { params });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },

  async getPeriodUsage(
    period: 'today' | 'yesterday' | 'last7days' | 'last30days' | 'all',
    projectId?: string,
  ): Promise<any> {
    const params: any = { period };
    if (projectId) {
      params.projectId = projectId;
    }

    try {
      const response = await chatClient.get('/usage/period', { params });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  },
};
